// app/(main)/compte/layout.tsx

import { User, Package, Heart, LogOut, ChevronRight, LayoutDashboard } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import HorizontalTab, { TabItem } from "../components/ui/HorizontalTab";
import { useAuth } from "@cabukaka/hooks";
import { PageContainer } from "../components/ui/PageContainer";

interface MenuItem {
    icon: typeof User;
    label: string;
    value: string;
}

export default function AccountLayout({ children }: { children: React.ReactNode }) {
    const navigateTo = useNavigate();
    const location = useLocation()
    const pathname = location.pathname;
    const { user, logout } = useAuth()

    const menuItems: MenuItem[] = [
        { icon: LayoutDashboard, label: "Vue d'ensemble", value: "/compte" },
        { icon: Package, label: "Mes commandes", value: "/compte/mes-commandes" },
        { icon: Heart, label: "Mes favoris", value: "/compte/favoris" },
        { icon: User, label: "Mon profil", value: "/compte/profil" },
    ];

    const handleLogout = () => {
        logout()
        navigateTo("/");
    };

    const tabItems: TabItem[] = menuItems.map(item => ({
        icon: item.icon,
        label: item.label,
        value: item.value
    }));

    const handleTabChange = (value: string) => {
        navigateTo(value);
    };

    return (
        <PageContainer className="py-4 md:py-6   lg:py-8">
            {/* Header de la page */}


            {/* HorizontalTab - UNIQUEMENT SUR MOBILE, EN HAUT */}
            <div className="lg:hidden mb-6 px-4 mt-4">
                <HorizontalTab
                    tabItems={tabItems}
                    defaultValue={pathname}
                    onChange={handleTabChange}
                    showScrollButtons={true}
                />
            </div>

            <div className="flex flex-col lg:grid lg:grid-cols-4 gap-6 md:gap-8">
                {/* Sidebar - DESKTOP UNIQUEMENT */}
                <div className="hidden lg:block lg:col-span-1">
                    <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5 sticky top-[140px]">
                        <div className="text-center pb-4 mb-4 border-b border-gray-100">
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                <User className="w-8 h-8 md:w-10 md:h-10 text-orange-500" />
                            </div>
                            <h2 className="font-semibold text-gray-900 text-base md:text-lg">
                                {user?.username}
                            </h2>
                            <p className="text-xs md:text-sm text-gray-500 mt-1">
                                {user?.email}
                            </p>
                        </div>

                        <div className="space-y-1">
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = pathname === item.value;
                                return (
                                    <button
                                        key={item.value}
                                        onClick={() => navigateTo(item.value)}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${isActive
                                            ? "bg-orange-500/10 text-orange-500"
                                            : "hover:bg-gray-50 text-gray-700"
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon className={`w-4 h-4 md:w-5 md:h-5 ${isActive ? "text-orange-500" : "text-gray-400"}`} />
                                            <span className="text-sm md:text-base">{item.label}</span>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                    </button>
                                );
                            })}
                        </div>

                        <div className="pt-4 mt-4 border-t border-gray-100">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 p-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <LogOut className="w-4 h-4 md:w-5 md:h-5" />
                                <span className="text-sm md:text-base">Se déconnecter</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Contenu principal */}
                <div className="lg:col-span-3">
                    {children}
                </div>
            </div>
        </PageContainer>
    );
}